import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

const ACTION_UNPUBLISH = "unpublish";
const ACTION_PUBLISH = "publish";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

const ENDPOINT = "/shopify/publication-update";

// Pre-selected channel. The sale-day case is Online Store only — Point of Sale
// must keep selling the product in store.
const DEFAULT_CHANNEL_NAME = "Online Store";

// Identifier the backend should key on. Values must match the backend's
// IDENTIFIER_COLUMNS in product_tag_adder.py; "" is the auto-cascade.
const IDENTIFIER_OPTIONS = [
  { value: "", label: "Auto (try SKU, then Product ID, then Handle)" },
  { value: "handle", label: "HANDLE — recommended" },
  { value: "sku", label: "SKU / Parent SKU / Variant SKU" },
  { value: "shopify_product_id", label: "SHOPIFY_PRODUCT_ID" },
  { value: "title", label: "Product Title" },
];

const IDENTIFIER_HELPER = {
  "": "Tries SKU, then SHOPIFY_PRODUCT_ID, then HANDLE — whichever columns your file has.",
  handle:
    "Handle is the best key here: publication state is per product, and a handle always identifies exactly one product.",
  sku:
    "SKU search is a prefix match, so a short parent SKU can resolve to a different product. Check the report's Matched Title column before running live.",
  shopify_product_id: "Exact match on the Shopify product GID or numeric ID.",
  title: "Exact product title, case-insensitive. Less precise than handle.",
};

// "YYYY-MM-DDTHH:mm" in the user's local time — what <input type="datetime-local"> expects.
const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const ACTION_LABEL = {
  [ACTION_UNPUBLISH]: "Unpublish",
  [ACTION_PUBLISH]: "Publish",
};


function PublicationUpdateTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [action, setAction] = useState(ACTION_UNPUBLISH);
  const [identifierColumn, setIdentifierColumn] = useState("handle");
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState("");
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [dryRun, setDryRun] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: `${ENDPOINT}/jobs`,
    cacheKey: "publicationUpdate.jobs",
    pageSize: 10,
  });

  // Publication ids are opaque Shopify GIDs, so the list has to come from the
  // backend rather than being hard-coded here.
  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelsError("");
    try {
      const response = await flaskApi.get("/shopify/publication-channels");
      const list = response.data?.channels || [];
      setChannels(list);
      const preselect = list.find((c) => c.name === DEFAULT_CHANNEL_NAME);
      setSelectedChannels(preselect ? [preselect.id] : []);
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to load sales channels."
      );
      setChannelsError(message);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const toggleChannel = (id) => {
    setSelectedChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectedChannelNames = channels
    .filter((c) => selectedChannels.includes(c.id))
    .map((c) => c.name);

  const toolInfo = {
    toolName: "Publication Update",
    description:
      "Publish or unpublish Shopify products on chosen sales channels from an uploaded CSV/XLSX. Only channel availability changes — price, tags, template and inventory are untouched. Runs as a background job.",
    steps: [
      "Choose Unpublish (take products off a channel) or Publish (put them back).",
      "Pick the sales channels. Online Store is pre-selected — the sale-day case is removing products from the Online Store while Point of Sale keeps selling them.",
      "Upload a CSV or XLSX and pick which column identifies the product. HANDLE is recommended: publication state is per product.",
      "Leave Dry run ticked for the first pass. It resolves every product, reads its current per-channel state, and reports exactly what would change without touching Shopify.",
      "Review the report, then re-run with Dry run unticked to apply it.",
      "Optionally schedule it — the file uploads now and the job fires at the time you pick, so an unpublish can land exactly when sale pricing goes live.",
    ],
    templates: [],
  };

  const guideTips = [
    "Why this exists: on sale days prices drop below online MAP, so MAP-protected products have to come off the Online Store. Unpublishing hides the product from the storefront but leaves Point of Sale, price, tags and inventory alone.",
    "Dry run is the safety net and is ON by default. Unpublishing is visible to customers immediately, so always dry-run a new file first.",
    "Publication state is per PRODUCT, not per variant. A variant-level export repeats the same handle many times; duplicate rows are detected and reported as 'duplicate_row' rather than applied again.",
    "Rows already in the desired state come back as 'no_change' — no Shopify write happens, so re-running is safe.",
    "To reverse a sale-day unpublish, run the same file with action = Publish. The report's 'Published Channels (before)' column records what each product's state was.",
    "Scheduled jobs are persisted server-side and survive deploys. Cancel a scheduled job before it runs if plans change; once started it runs to completion.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prev = previous[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(
            `Publication job ${j.job_id} complete. Report is ready.`,
            "success"
          );
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Publication job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Publication job ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }
    if (!selectedChannels.length) {
      addToast("Pick at least one sales channel.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }

    // A live run changes what customers can see, so make it an explicit choice.
    if (!dryRun) {
      const verb = action === ACTION_UNPUBLISH ? "REMOVE" : "ADD";
      const preposition = action === ACTION_UNPUBLISH ? "from" : "to";
      const ok = window.confirm(
        `LIVE RUN — this will ${verb} the products in "${inputFile.name}" ` +
          `${preposition} ${selectedChannelNames.join(", ")} on Shopify.\n\n` +
          (action === ACTION_UNPUBLISH
            ? "Unpublished products disappear from the storefront immediately.\n\n"
            : "") +
          "Continue?"
      );
      if (!ok) return;
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    formData.append("action", action);
    formData.append("channels", selectedChannels.join(","));
    formData.append("dry_run", dryRun ? "true" : "false");
    if (identifierColumn) {
      formData.append("identifier_column", identifierColumn);
    }
    if (includeArchived) {
      formData.append("include_archived", "true");
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post(ENDPOINT, formData, {
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(pct);
        },
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      const modeWord = dryRun ? "Dry-run" : "Live";
      if (scheduledIso) {
        addToast(
          `${modeWord} ${action} job ${jobId} scheduled for ${formatScheduledDisplay(
            scheduledIso
          )}.`,
          "info"
        );
      } else {
        addToast(
          `${modeWord} ${action} job ${jobId} queued. Tracking progress…`,
          "info"
        );
      }
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to start publication job."
      );
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled publication job ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`${ENDPOINT}/jobs/${job.job_id}`);
      addToast(`Job ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel job.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "publication_update_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download report."
      );
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.input_filename || "input_file"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download input file."
      );
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `${ENDPOINT}/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Publication job ${job.job_id} rescheduled for ${formatScheduledDisplay(
              scheduledIso
            )}.`
          : `Publication job ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Publication Update"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Action</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="Action"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="publication-action"
                      value={ACTION_UNPUBLISH}
                      checked={action === ACTION_UNPUBLISH}
                      onChange={() => setAction(ACTION_UNPUBLISH)}
                      disabled={uploading}
                    />
                    <span>Unpublish (remove from channel)</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="publication-action"
                      value={ACTION_PUBLISH}
                      checked={action === ACTION_PUBLISH}
                      onChange={() => setAction(ACTION_PUBLISH)}
                      disabled={uploading}
                    />
                    <span>Publish (add back to channel)</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    Only channel availability changes. Price, compare-at, tags,
                    template, inventory and product status are never touched.
                  </span>
                </div>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Sales Channels</h3>
              </div>
              <div className="tool-field-stack">
                {channelsLoading ? (
                  <p className="tool-helper-text">Loading sales channels…</p>
                ) : channelsError ? (
                  <>
                    <div className="tool-job-error">{channelsError}</div>
                    <button
                      type="button"
                      className="tool-btn-secondary"
                      onClick={loadChannels}
                    >
                      Retry
                    </button>
                  </>
                ) : (
                  <>
                    <p className="tool-helper-text">
                      Online Store is pre-selected. Leave Point of Sale alone
                      unless you intend to stop in-store sales too.
                    </p>
                    <div className="shopify-checkbox-row">
                      {channels.map((channel) => (
                        <label
                          key={channel.id}
                          className="shopify-checkbox-label"
                        >
                          <input
                            type="checkbox"
                            checked={selectedChannels.includes(channel.id)}
                            onChange={() => toggleChannel(channel.id)}
                            disabled={uploading}
                          />
                          {channel.name}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product list"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText="CSV or XLSX. Needs the column you pick below — HANDLE, SKU / Parent SKU, SHOPIFY_PRODUCT_ID, or Product Title."
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Identify Products By</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="publication-identifier">
                  Column
                </label>
                <select
                  id="publication-identifier"
                  className="tool-input"
                  value={identifierColumn}
                  onChange={(event) => setIdentifierColumn(event.target.value)}
                  disabled={uploading}
                >
                  {IDENTIFIER_OPTIONS.map((opt) => (
                    <option key={opt.value || "auto"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {IDENTIFIER_HELPER[identifierColumn]}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">
                  Tab Name{" "}
                  <span className="tool-section-optional">(optional)</span>
                </h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank
                  to use the first tab. Case-sensitive.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Products"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Safety</h3>
              </div>
              <div className="tool-field-stack">
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(event) => setDryRun(event.target.checked)}
                    disabled={uploading}
                  />
                  Dry run — report what would change, without touching Shopify
                </label>
                <span className="tool-helper-text">
                  {dryRun
                    ? "Safe. Every product is resolved and its current per-channel state read, but no change is made."
                    : "LIVE — changes are applied to Shopify. Unpublished products disappear from the storefront immediately."}
                </span>
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) =>
                      setIncludeArchived(event.target.checked)
                    }
                    disabled={uploading}
                  />
                  Also match archived products
                </label>
                <span className="tool-helper-text">
                  By default only active products are matched. Archived products
                  are already off every channel, so this is rarely needed. Draft
                  products are always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="publication-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="publication-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label
                      className="tool-label"
                      htmlFor="publication-scheduled-at"
                    >
                      Date &amp; time
                    </label>
                    <input
                      id="publication-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file uploads now and the
                      job fires at the scheduled time — handy for landing an
                      unpublish exactly when sale pricing goes live.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading || channelsLoading}
              >
                {uploading
                  ? "Uploading…"
                  : `${runTime === RUN_SCHEDULE ? "Schedule" : "Start"} ${
                      dryRun ? "Dry Run" : `Live ${ACTION_LABEL[action]}`
                    }`}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Publication Jobs"
          emptyText="No publication jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct =
              total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            const jobAction = j.action === ACTION_PUBLISH ? ACTION_PUBLISH : ACTION_UNPUBLISH;
            // dry_run may be absent on an in-flight doc; progress carries it too.
            const jobDryRun =
              j.dry_run !== undefined ? j.dry_run : progress.dry_run;
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Action</span>
                  <span className="tool-job-value">
                    {ACTION_LABEL[jobAction]}
                    {jobDryRun === false ? " (LIVE)" : jobDryRun ? " (dry run)" : ""}
                  </span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Channels</span>
                  <span className="tool-job-value">
                    {j.channel_names || progress.channels || "—"}
                  </span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Input</span>
                  <span className="tool-job-value">{j.input_filename || "—"}</span>
                </div>
                {j.sheet_name ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Tab</span>
                    <span className="tool-job-value">{j.sheet_name}</span>
                  </div>
                ) : null}
                {j.scheduled_at ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Scheduled</span>
                    <span className="tool-job-value">
                      {formatScheduledDisplay(j.scheduled_at)}
                    </span>
                  </div>
                ) : null}
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>
                    {j.status}
                  </span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} (
                        {pct}%)
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </>
                ) : null}
                <div className="tool-job-counts">
                  <span>Updated: {progress.updated || 0}</span>
                  <span>Would change: {progress.planned || 0}</span>
                  <span>No change: {progress.no_change || 0}</span>
                  <span>Duplicates: {progress.duplicates || 0}</span>
                  <span>Not found: {progress.not_found || 0}</span>
                  <span>Errors: {progress.errors || 0}</span>
                  <span>Skipped: {progress.skipped || 0}</span>
                </div>
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id
                      ? "Downloading…"
                      : "Download Report"}
                  </button>
                ) : null}
                {j.status === STATUS_SCHEDULED ? (
                  <button
                    type="button"
                    className="tool-btn-secondary"
                    onClick={() => handleCancel(j)}
                    disabled={cancellingJobId === j.job_id}
                  >
                    {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                  </button>
                ) : null}
                <JobInputActions
                  job={j}
                  onDownloadInput={handleDownloadInput}
                  downloadingInput={downloadingInputJobId === j.job_id}
                  onRestart={handleRestart}
                  restarting={restartingJobId === j.job_id}
                  minScheduledAt={minScheduledAt}
                />
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default PublicationUpdateTool;
